import { useState, useEffect, useMemo } from 'react';
import { createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { formatDate } from '../../utils/date';
import "../../styles/GrievancesTable.css";
import apiClient from '../../Api/apiConfig';

const columnHelper = createColumnHelper();

const GrievancesTable = () => {
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [selectedGrievance, setSelectedGrievance] = useState(null);

  useEffect(() => {
    const fetchGrievancesAndThemes = async () => {
      try {
        const [grievancesResponse, themesResponse, landlordsResponse] = await Promise.all([
          apiClient.get('/grievances'),
          apiClient.get('/themes'),
          apiClient.get('/landlords'),
        ]);

        const themesMap = themesResponse.data.reduce((acc, theme) => {
          acc[theme.id] = theme.name;
          return acc;
        }, {});

        const landlordsMap = landlordsResponse.data.reduce((acc, landlord) => {
          acc[landlord.id] = landlord.name;
          return acc;
        }, {});

        const grievancesWithDetails = grievancesResponse.data.map(grievance => ({
          ...grievance,
          theme_name: themesMap[grievance.theme_id] || 'Non défini',
          landlord_name: landlordsMap[grievance.landlord_id] || 'Non défini',
        }));

        setData(grievancesWithDetails);
        setLoading(false);
      } catch (err) {
        setError('Erreur lors de la récupération des doléances, des thèmes ou des bailleurs.');
        setLoading(false);
      }
    };

    fetchGrievancesAndThemes();
  }, []);

  // Options de statut en accord avec votre table (enum)
  const statutOptions = {
    "new": "Nouveau",
    "en cours": "En cours",
    "résolu": "Résolu",
    "fermé": "Fermé",
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      // Appel PUT sur /api/grievances/:id en envoyant le champ "statuts"
      await apiClient.put(`/grievances/${id}`, { statuts: newStatus });
      setData(data.map(grievance =>
        grievance.id === id ? { ...grievance, statuts: newStatus } : grievance
      ));
    } catch (err) {
      console.error('Erreur lors de la mise à jour du statut :', err);
      alert('Erreur lors de la mise à jour du statut.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette doléance ?')) {
      try {
        await apiClient.delete(`/grievances/${id}`);
        setData(data.filter(grievance => grievance.id !== id));
      } catch (err) {
        console.error('Erreur lors de la suppression de la doléance :', err);
        alert('Erreur lors de la suppression de la doléance.');
      }
    }
  };

  const openDetailsPopup = (grievance) => {
    setSelectedGrievance(grievance);
    setShowDetailsPopup(true);
  };

  const closeDetailsPopup = () => {
    setSelectedGrievance(null);
    setShowDetailsPopup(false);
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('reference', {
        header: 'Référence',
        cell: (info) => info.getValue() || 'N/A',
      }),
      columnHelper.accessor('title', {
        header: 'Titre',
        cell: (info) => info.getValue() || 'Sans titre',
      }),
      columnHelper.accessor('theme_name', {
        header: 'Thème',
        cell: (info) => info.getValue() || 'Non défini',
      }),
      columnHelper.accessor('landlord_name', {
        header: 'Bailleur',
        cell: (info) => info.getValue() || 'Non défini',
      }),
      columnHelper.accessor('statuts', {
        header: 'Statut',
        cell: (info) => (
          <select
            value={info.getValue()}
            onChange={(e) => handleStatusChange(info.row.original.id, e.target.value)}
          >
            {Object.entries(statutOptions).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        ),
      }),
      columnHelper.accessor('created_at', {
        header: 'Date',
        cell: (info) => (info.getValue() ? formatDate(info.getValue()) : 'Non spécifié'),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => (
          <>
            <button onClick={() => openDetailsPopup(info.row.original)}>Détails</button>
            <button onClick={() => handleDelete(info.row.original.id)}>Supprimer</button>
          </>
        ),
      }),
    ],
    [data]
  );

  const filteredData = useMemo(() => data || [], [data]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (loading) return <p>Chargement des données...</p>;
  if (error) return <p className="error-message">{error}</p>;

  return (
    <>
      <table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {showDetailsPopup && selectedGrievance && (
        <div className="popup-AdminGrievances">
          <div className="popup-content-AdminGrievances">
            <h3>Détails de la Doléance</h3>
            <p><strong>Référence :</strong> {selectedGrievance.reference}</p>
            <p><strong>Titre :</strong> {selectedGrievance.title}</p>
            <p><strong>Thème :</strong> {selectedGrievance.theme_name}</p>
            <p><strong>Bailleur :</strong> {selectedGrievance.landlord_name}</p>
            <p><strong>Statut :</strong> {statutOptions[selectedGrievance.statuts]}</p>
            <p><strong>Description :</strong> {selectedGrievance.description}</p>
            <p><strong>Date de création :</strong> {formatDate(selectedGrievance.created_at)}</p>
            <p><strong>Date de mise à jour :</strong> {formatDate(selectedGrievance.updated_at)}</p>
            <button onClick={closeDetailsPopup}>Fermer</button>
          </div>
        </div>
      )}
    </>
  );
};

export default GrievancesTable;
